import { useMemo, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import type { Item } from '@mas-flow/types';
import { useItems, useAnchorItem } from '../hooks/useItems';
import { useToast } from '../hooks/useToast';
import { ApiHttpError } from '../api/client';

interface AddAnchorPickerProps {
  onClose: () => void;
}

export function AddAnchorPicker({ onClose }: AddAnchorPickerProps) {
  const { data: itemsAll } = useItems();
  const setAnchor = useAnchorItem();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Item | null>(null);
  const [why, setWhy] = useState('');

  const candidates = useMemo(() => {
    const pool = (itemsAll ?? []).filter((it) => !it.isAnchor && it.lifecycle !== 'Archived');
    const sorted = [...pool].sort((a, b) => (b.wsjf ?? -1) - (a.wsjf ?? -1));
    if (!q.trim()) return sorted;
    const needle = q.trim().toLowerCase();
    return sorted.filter((it) => (it.title ?? '').toLowerCase().includes(needle));
  }, [itemsAll, q]);

  const allAnchorsOrArchived = (itemsAll ?? []).every((it) => it.isAnchor || it.lifecycle === 'Archived');

  const confirm = () => {
    if (!selected || !why.trim()) return;
    setAnchor.mutate(
      { id: selected.id, isAnchor: true, anchorWhy: why.trim(), etag: selected._etag },
      {
        onSuccess: () => {
          toast('Anchor flagged');
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiHttpError && err.status === 412) {
            toast('Item changed elsewhere — try again');
          } else if (err instanceof ApiHttpError && err.status === 403) {
            toast("You don't have permission to flag anchors");
          } else {
            toast("Couldn't flag anchor");
          }
        },
      },
    );
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Add anchor" className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
      <FocusTrap focusTrapOptions={{ escapeDeactivates: false, clickOutsideDeactivates: false, tabbableOptions: { displayCheck: 'none' } }}>
      <div className="w-[520px] max-w-[90vw] rounded border border-slate-200 bg-white p-4 shadow-xl">
        <h2 className="text-lg font-semibold">Flag an existing item as an anchor</h2>
        {!selected ? (
          <>
            <input
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search items by title…"
              className="mt-3 w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
            <div className="mt-2 max-h-64 overflow-y-auto rounded border border-slate-200">
              {candidates.length === 0 ? (
                <div className="p-3 text-sm italic text-slate-500">
                  {allAnchorsOrArchived
                    ? 'No more items to flag — all current items are already anchors.'
                    : 'No items match.'}
                </div>
              ) : (
                <ul>
                  {candidates.map((it) => (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => setSelected(it)}
                        className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      >
                        <span className="truncate">{it.title}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          {it.wsjf != null ? `WSJF ${it.wsjf.toFixed(1)}` : 'unscored'} · {it.categoryId}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <button type="button" onClick={onClose} className="rounded border border-slate-300 px-3 py-1 text-xs">
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm">
              Selected: <span className="font-medium">{selected.title}</span>
            </p>
            <label className="mt-3 block text-xs">
              <span className="font-medium text-slate-700">Why is this an anchor? (required)</span>
              <input
                type="text"
                autoFocus
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                maxLength={140}
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => setSelected(null)} className="rounded border border-slate-300 px-3 py-1 text-xs">
                Back
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={!why.trim() || setAnchor.isPending}
                className="rounded bg-amber-600 px-3 py-1 text-xs text-white disabled:opacity-50"
              >
                {setAnchor.isPending ? 'Flagging…' : 'Flag as anchor'}
              </button>
            </div>
          </>
        )}
      </div>
      </FocusTrap>
    </div>
  );
}
